/**
 * NumericFieldBox — pins a numeric field's CONTROL BOX to a left-to-right base
 * direction, so the digits and the unit affix beside them keep their positions
 * in every locale.
 *
 * ## The defect it exists to prevent
 *
 * `components/ui/input.tsx` gives every input element `dir="auto"`, which is right:
 * a typed number is a run of European digits, so the field's own direction
 * resolves LTR and the value is laid out (and left-aligned) left-to-right even
 * on a Hebrew page. The affix beside it does NOT follow: it is positioned with
 * Tailwind's direction-aware utilities (`absolute end-3`, `pe-8`, `ps-10`) or
 * laid out as a flex sibling, and both resolve against the RTL *container*, not
 * against the LTR input. So under `dir="rtl"` the two disagree — the `%` lands at the box's
 * LEFT edge, exactly where the LTR-aligned digits start, and the value renders
 * as "%123" with the symbol overlapping the first digit (WI 75627715, reported
 * on the tax-rate dialog and true of every percentage, currency, date and time
 * field in the app at once).
 *
 * Nothing else in the pipeline can see it: `tsc` reads a className as a string,
 * ESLint sees ordinary JSX, jsdom computes no layout, and a rendered test
 * asserting the `%` is present passes — it IS present, on top of the number.
 * Only a screenshot in an RTL locale shows it.
 *
 * ## The convention
 *
 * **A numeric field's control box is LTR.** The input and any NON-LINGUISTIC
 * affix it carries — `%`, a currency code, a unit abbreviation, a range dash, a
 * calendar/clock trigger — live inside one `dir="ltr"` box, so the digits read
 * left-to-right and the affix sits at the box's right edge in every locale.
 *
 * ## Inside the box, an inset is spelled PHYSICALLY — `right-3`, not `end-3`
 *
 * This is the part that is not guessable, and pinning the box alone does NOT fix
 * the affix without it. `tailwindcss-rtl` (see `apps/frontend/tailwind.config.js`)
 * implements the inset utilities as DESCENDANT selectors rather than as CSS
 * logical properties:
 *
 *     [dir="rtl"] .end-3 { left:  0.75rem }
 *     [dir="ltr"] .end-3 { right: 0.75rem }
 *
 * A `dir="ltr"` nested inside an RTL document does not escape the outer
 * `[dir="rtl"]` ancestor — it merely ADDS the second rule. Both match, the affix
 * receives `left` AND `right`, and it stretches the full width of the field with
 * its glyph back on the digits. Measured in Chrome: the `%` span rendered 850px
 * wide inside an 874px box. Physical `right-*` / `left-*` are untouched by that
 * plugin and by any ancestor's `dir`, and inside a box that is LTR by definition
 * they are exactly as portable as the logical spelling.
 *
 * PADDING is the opposite and must stay logical: the plugin emits `ps-*` / `pe-*`
 * as native `padding-inline-*`, which resolve against the ELEMENT's own direction
 * — and the input's `dir="auto"` resolves LTR for digits — so `pe-8` correctly
 * reserves space on the right. Do not "normalise" those to `pr-8`.
 *
 * The test for the plugin's other descendant-selector families is whether the two
 * arms set the SAME property. `text-end` does (`text-align` twice), so the later
 * `[dir="ltr"]` rule simply wins and it is safe under a nested `dir` — measured.
 * The inset pair sets `left` on one arm and `right` on the other, so neither
 * overrides the other and both apply; `rounded-s/e-*` and `float`/`clear` share
 * that shape. See `docs/design-system/ui-i18n.md` for the full table.
 *
 * ## What NOT to wrap
 *
 * The box is the CONTROL, not the field. Leave outside it:
 *
 * - the `<Label>`, helper text and validation copy — translated prose, which
 *   must inherit the document direction (STYLE_GUIDE § Bidirectional Text: do
 *   not slap `dir` on our own copy);
 * - a translated unit WORD ("days", "hours", "ימים") rendered beside the
 *   number. Under RTL the flex row reverses and the pair still reads correctly
 *   in reading order — pinning it LTR would be the regression;
 * - a `<Select>`/picker whose options are translated words or entity names (a
 *   UoM picker, a currency picker, a period picker). Same reason: its own copy
 *   is language text, and the number-then-unit reading order survives the flip.
 *
 * This is the editable counterpart to {@link LtrToken}, which does the same job
 * for a rendered value (`<bdi dir="ltr">`). Use LtrToken for display, this for
 * an input.
 *
 * ## Usage
 *
 *   <NumericFieldBox className="relative">
 *     <Input inputMode="decimal" className="pe-8" … />
 *     <span className="absolute right-3 top-1/2 -translate-y-1/2">%</span>
 *   </NumericFieldBox>
 *
 * Most call sites get this for free: the shared localized inputs
 * (`LocalizedPercentageInput`, `LocalizedMonetaryInput`, `LocalizedDateInput`,
 * `LocalizedTimeInput`, `MeasurementInput`, `MarkupInput`, …) already render
 * their control box through it. Reach for it directly only when a surface
 * hand-rolls a numeric input with a symbol affix beside it.
 *
 * Enforced by `tools/check_rtl_numeric_affix.py`.
 */

import React from 'react';

type NumericFieldBoxProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
};

export const NumericFieldBox = React.forwardRef<HTMLDivElement, NumericFieldBoxProps>(
  function NumericFieldBox({ children, ...props }, ref) {
    // `dir` is spelled BEFORE the spread so a caller can override it, and as a
    // literal so `tools/check_rtl_numeric_affix.py` — which reads what the tag
    // spells — can credit this element as the box it is looking for.
    return (
      <div dir="ltr" ref={ref} {...props}>
        {children}
      </div>
    );
  }
);
